/**
 * Quell-Metadaten im Ticket-Detail (Spec 5.2).
 *
 * Die Anzeige folgt `sources.metadata_display`: Beschriftung, Reihenfolge und
 * Format je Schluessel. **Unbekannte Schluessel verschwinden nicht** — sie
 * stehen hinten und bekommen ein Format nach ihrem Typ. Genau das verlangt die
 * Spec: „Unbekannte Keys werden trotzdem gespeichert und generisch angezeigt."
 *
 * Die Sortier- und Format-Logik liegt in `buildMetadataEntries()`
 * (`@tickethub/shared`) und ist dort getestet; hier steht nur das Rendern.
 */
import { buildMetadataEntries, type MetadataDisplay, type MetadataEntry } from '@tickethub/shared';
import { useTranslation } from 'react-i18next';

export type TicketMetadataProps = {
  metadata: Record<string, unknown>;
  display: MetadataDisplay | null;
};

function Value({ entry }: { entry: MetadataEntry }) {
  const { t } = useTranslation();
  const { format, value } = entry;

  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (format === 'boolean') {
    return <span>{value === true ? t('boolean.yes') : t('boolean.no')}</span>;
  }

  if (format === 'link' && typeof value === 'string') {
    return (
      <a className="break-all underline" href={value} target="_blank" rel="noreferrer noopener">
        {value}
      </a>
    );
  }

  // Konsolenausgaben und Stacktraces: als Codeblock, Zeile fuer Zeile.
  if (format === 'code') {
    const text = Array.isArray(value)
      ? value.map((line) => String(line)).join('\n')
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);
    return (
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs">
        {text}
      </pre>
    );
  }

  if (format === 'list' && Array.isArray(value)) {
    return (
      <ul className="list-inside list-disc">
        {value.map((item, index) => (
          <li key={index} className="break-all">
            {typeof item === 'string' ? item : JSON.stringify(item)}
          </li>
        ))}
      </ul>
    );
  }

  if (format === 'json') {
    return (
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return <span className="break-all">{String(value)}</span>;
}

export function TicketMetadata({ metadata, display }: TicketMetadataProps) {
  const { t } = useTranslation();
  const entries = buildMetadataEntries(metadata, display);

  if (entries.length === 0) return null;

  return (
    <section className="space-y-2 rounded-lg border p-4">
      <h2 className="text-sm font-medium">{t('ticket.metadata')}</h2>
      <dl className="space-y-2 text-sm">
        {entries.map((entry) => (
          <div key={entry.key} className="space-y-1">
            <dt className="text-xs text-muted-foreground">{entry.label}</dt>
            <dd>
              <Value entry={entry} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
